import { useFetchPage } from "~/core/composables/useFetchPage";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { ContentResource } from "~/core/models/ContentResource.interface";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_GREATER_OR_EQUAL,
  OPERATOR_LOWER_OR_EQUAL,
  OPERATOR_RSQL_ALL_MATCH,
  OPERATOR_RSQL_EQUAL,
  OPERATOR_RSQL_ILIKE,
} from "~/core/models/filters";
import ContentService from "~/core/services/content.service";

export function useFetchContents({
  organizationCode,
  searchMode,
  useQueryParametersForPagination,
}: UseFetchParameters): UseFetchResponse<ContentResource> {
  const service = ContentService.findContentResources.bind(ContentService);

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.DTWIN_UID,
      {
        key: "deviceTwinUid",
        operator: OPERATOR_RSQL_EQUAL,
        value: "",
      },
    ],
    [
      Filters.DTWIN_CONFIGURATION_VERSION_NAME_OR_VERSION,
      {
        key: ["descriptor.versionName", "descriptor.version"],
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
      },
    ],
    [
      Filters.DTWIN_CONFIGURATION_START_DATE,
      {
        key: "modifiedAt",
        operator: OPERATOR_GREATER_OR_EQUAL,
        value: "",
      },
    ],
    [
      Filters.DTWIN_CONFIGURATION_END_DATE,
      {
        key: "modifiedAt",
        operator: OPERATOR_LOWER_OR_EQUAL,
        value: "",
      },
    ],
    [
      Filters.CONTENT_TAGS,
      {
        key: "tags",
        operator: OPERATOR_RSQL_ALL_MATCH,
        value: [],
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, allResults, fetchData, fetchAllData } =
    useFetchPage({
      organizationCode,
      service,
      searchParams,
      sort,
      useQueryParametersForPagination,
    });

  watch(sort, async () => await search(true));

  return {
    isLoading,
    results,
    allResults,
    error,
    filters,
    fetchData,
    fetchAllData,
    setFilter,
    onSortChange,
    search,
    onSearch,
  };
}
